/**
 * Global Site Footer.
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {
    RenderState,
    useRenderState,
    View,
} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {actionStyles} from "@khanacademy/wonder-blocks-styles";
import {color, font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingXSmall,
    LabelXSmall,
    Tagline,
} from "@khanacademy/wonder-blocks-typography";
import {GoogleAnalytics} from "@khan/analytics-google";
import {doNotTranslate} from "@khan/i18n";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {a11yStyles, maxContainerWidth, mediaQueries} from "@khan/shared-styles";
import {redirect} from "@khan/urls";

import {
    getZendeskArticleLink,
    getZendeskCommunityLink,
    getZendeskLink,
} from "#zendesk/zendesk-util.ts";

import DonateLink from "./donate-link.tsx";
import FooterDomains from "./footer-domains.tsx";
import InternationalContentLinks from "./international-content-links.tsx";
import LanguagePicker from "./language-picker.tsx";
import AppStoreBadge from "./store-badges/app-store-badge.tsx";
import GooglePlayBadge from "./store-badges/google-play-badge.tsx";

import facebookIcon from "@phosphor-icons/core/fill/facebook-logo-fill.svg";
import instagramIcon from "@phosphor-icons/core/fill/instagram-logo-fill.svg";
import twitterIcon from "@phosphor-icons/core/fill/twitter-logo-fill.svg";

// This is a temporary color for the non-link text. We will be revisiting our dark UI
// color system at a future time. At the time of writing, we don't expect to use this
// color anywhere else.
const NON_LINK_COLOR = "#8295BA";

// For social media.
const twitterUrl = "https://twitter.com/khanacademy";
const facebookUrl = "https://www.facebook.com/khanacademy";
const instagramUrl = "https://instagram.com/khanacademy";

// For our mobile apps
const playStoreUrl =
    "https://play.google.com/store/apps/details?id=org.khanacademy.android&referrer=utm_source%3Dwebsite%2520footer%26utm_medium%3Dwebsite%2520footer%26utm_campaign%3Dwebsite%2520footer";
const appStoreUrl =
    "https://itunes.apple.com/app/apple-store/id469863705?pt=698519&ct=website%20footer&mt=8";

const CookiePreferencesItem = () => {
    const renderState = useRenderState();

    const showCookiePreferences =
        renderState === RenderState.Standard &&
        !RequestInfo.isE2E &&
        !RequestInfo.isBot;
    if (!showCookiePreferences) {
        return null;
    }

    return (
        <li className={css(styles.li)}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
            <Link
                style={actionStyles.inverse}
                href="#"
                onClick={() => {
                    // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                    window.Optanon.ToggleInfoDisplay();
                }}
            >
                {t`Cookie Preferences`}
            </Link>
        </li>
    );
};

class SiteFooter extends React.Component<Record<any, any>> {
    _triggerListenersAfterMount: number | null | undefined;
    static _mountListeners: Array<() => unknown> = [];
    static addMountListener(listener: () => unknown) {
        SiteFooter._mountListeners.push(listener);
    }
    static removeMountListener(listener: () => unknown) {
        SiteFooter._mountListeners = SiteFooter._mountListeners.filter(
            (l) => l !== listener,
        );
    }

    componentDidMount() {
        // HACK(mdr): We load the footer asynchronously, which changes the flow
        //     of the page. Components that need to measure their distance from
        //     the bottom of the page can register a listener for SiteFooter mount
        //     events, which we'll call from here.
        //     Currently used in StickyModule.
        // NOTE(alex): We need to measure on the next frame to ensure that
        //     Aphrodite styles are ready.
        // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API
        // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
        // eslint-disable-next-line no-restricted-syntax
        this._triggerListenersAfterMount = setTimeout(function () {
            for (const listener of SiteFooter._mountListeners) {
                listener();
            }
        }, 0);
    }

    componentWillUnmount() {
        if (this._triggerListenersAfterMount) {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            clearTimeout(this._triggerListenersAfterMount);
        }
    }

    /**
     * Generate an array of React DOM elements which represent the columns
     * and groups which hold the links in the site footer.
     */
    renderMainLinks(): React.ReactElement<React.ComponentProps<"div">> {
        // TODO(drosile): clean up once translated!
        // https://khanacademy.atlassian.net/browse/INFRA-3704
        const ourFinances = t`Our finances`;
        const showOurFinances =
            RequestInfo.kaLocale === "en" || ourFinances !== "Our finances";

        return (
            <div aria-label={t`footer`} role="navigation">
                <ul
                    className={css(
                        styles.mainLinksSection,
                        this.props.khanmigoSidebarOpen &&
                            styles.__mainLinksSection,
                    )}
                >
                    <li className={css(styles.linkGroup)}>
                        <HeadingXSmall
                            tag="h3"
                            style={[styles.li, styles.heading]}
                        >
                            <Link style={actionStyles.inverse} href="/about">
                                {t`About`}
                            </Link>
                        </HeadingXSmall>
                        <ul>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="https://blog.khanacademy.org/news/"
                                >
                                    {t`News`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="https://blog.khanacademy.org/research/"
                                    target="_blank"
                                >
                                    {t`Impact`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/the-team"
                                >
                                    {t`Our team`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/our-interns"
                                >
                                    {t`Our interns`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/our-content-specialists"
                                >
                                    {t`Our content specialists`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/our-board"
                                >
                                    {t`Our leadership`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/our-supporters"
                                >
                                    {t`Our supporters`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/contribute/credits"
                                >
                                    {t`Our contributors`}
                                </Link>
                            </li>
                            {showOurFinances && (
                                <li className={css(styles.li)}>
                                    <Link
                                        style={actionStyles.inverse}
                                        href="https://khanacademyannualreport.org/#finances"
                                    >
                                        {ourFinances}
                                    </Link>
                                </li>
                            )}
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/careers"
                                    aria-label={t`Careers at Khan Academy`}
                                >
                                    {t`Careers`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/careers/interns"
                                >
                                    {t`Internships`}
                                </Link>
                            </li>
                            <CookiePreferencesItem />
                        </ul>
                    </li>
                    <li className={css(styles.linkGroup)}>
                        <HeadingXSmall
                            tag="h3"
                            style={[styles.li, styles.heading]}
                        >
                            {t`Contact`}
                        </HeadingXSmall>
                        <ul>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href={getZendeskLink()}
                                >
                                    {t`Help center`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href={getZendeskCommunityLink()}
                                >
                                    {t`Support community`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/stories"
                                >
                                    {t`Share your story`}
                                </Link>
                            </li>
                            <li className={css(styles.li)}>
                                <Link
                                    style={actionStyles.inverse}
                                    href={getZendeskArticleLink(
                                        "202483630",
                                        false,
                                    )}
                                >
                                    {t`Press`}
                                </Link>
                            </li>
                        </ul>
                    </li>
                    <li className={css(styles.linkGroup)}>
                        <HeadingXSmall
                            tag="h3"
                            style={[styles.li, styles.heading]}
                        >
                            <Link
                                style={actionStyles.inverse}
                                href="/downloads"
                            >
                                {t`Download our apps`}
                            </Link>
                        </HeadingXSmall>
                        <ul>
                            <li
                                className={css(styles.li, styles.appStoreBadge)}
                            >
                                <AppStoreBadge
                                    safeAppDownloadUrl={appStoreUrl}
                                    style={{width: "100px"}}
                                    light={true}
                                />
                            </li>
                            <li
                                className={css(styles.li, styles.appStoreBadge)}
                            >
                                <GooglePlayBadge
                                    safeAppDownloadUrl={playStoreUrl}
                                    style={{width: "100px"}}
                                    light={true}
                                />
                            </li>
                        </ul>
                    </li>

                    <li className={css(styles.linkGroup)} key="g-subjects">
                        <HeadingXSmall
                            tag="h3"
                            style={[styles.li, styles.heading]}
                        >
                            {t`Courses`}
                        </HeadingXSmall>
                        <FooterDomains style={styles.li} />
                    </li>
                </ul>
            </div>
        );
    }

    renderLanguagePicker(): React.ReactNode {
        if (PermissionsInfo.isLoggedIn) {
            return (
                <View
                    style={[
                        styles.languagePickerContainer,
                        this.props.khanmigoSidebarOpen &&
                            styles.__languagePickerContainer,
                    ]}
                >
                    <LabelXSmall>
                        <Link
                            style={actionStyles.inverse}
                            href="/settings/account"
                        >
                            {t`Change language`}
                        </Link>
                    </LabelXSmall>
                </View>
            );
        }
        return <LanguagePicker />;
    }

    handleSocialMediaClick(e: React.SyntheticEvent, gaEventName: string) {
        const url = (e.currentTarget as HTMLAnchorElement).href;
        GoogleAnalytics.sendToGA("event", "Click", gaEventName).then(() => {
            redirect(url);
        });
    }

    render(): React.ReactNode {
        const donateLink = (
            <DonateLink
                light={true}
                key="donate"
                urlParams={{
                    c_src2: "Webapp.footer",
                }}
            />
        );
        const volunteerLink = (
            <Link
                href="/contribute"
                style={actionStyles.inverse}
                key="volunteer"
            >
                {t`volunteer`}
            </Link>
        );

        const currentYear = new Date().getFullYear();
        return (
            <footer
                className={css(
                    styles.rootStyle,
                    this.props.khanmigoSidebarOpen && styles.__rootStyle,
                )}
            >
                <div
                    className={css(
                        styles.footerContainer,
                        this.props.khanmigoSidebarOpen &&
                            styles.__footerContainer,
                    )}
                >
                    <div
                        className={css(
                            styles.mainBlock,
                            this.props.khanmigoSidebarOpen &&
                                styles.__mainBlock,
                        )}
                    >
                        <div
                            className={css(
                                styles.missionAndLanguageSection,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__missionAndLanguageSection,
                            )}
                        >
                            <Tagline
                                tag="p"
                                style={[
                                    styles.missionParagraph,
                                    this.props.khanmigoSidebarOpen &&
                                        styles.__missionParagraph,
                                ]}
                            >
                                {t`Our mission is to provide a free, world-class education to anyone, anywhere.`}
                            </Tagline>

                            <Strut size={20} />

                            <Tagline
                                tag="p"
                                style={[
                                    styles.missionParagraph,
                                    this.props.khanmigoSidebarOpen &&
                                        styles.__missionParagraph,
                                ]}
                            >
                                {t`Khan Academy is a 501(c)(3) nonprofit organization.`}{" "}
                                <Trans>
                                    {donateLink} or {volunteerLink} today!
                                </Trans>
                            </Tagline>
                        </div>
                        <div
                            role="presentation"
                            aria-labelledby="site-navigation"
                        >
                            <h2
                                id="site-navigation"
                                className={css(a11yStyles.srOnly)}
                            >
                                {t`Site Navigation`}
                            </h2>
                            {this.renderMainLinks()}
                        </div>
                        <div
                            className={css(
                                styles.languageAndInternationalWrapper,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__languageAndInternationalWrapper,
                            )}
                        >
                            {this.renderLanguagePicker()}
                            {!PermissionsInfo.isLoggedIn && (
                                <div
                                    className={css(
                                        styles.countryLinkContainer,
                                        this.props.khanmigoSidebarOpen &&
                                            styles.__countryLinkContainer,
                                    )}
                                >
                                    <InternationalContentLinks />
                                </div>
                            )}
                        </div>
                    </div>
                    <View
                        style={[
                            styles.bottomBlock,
                            this.props.khanmigoSidebarOpen &&
                                styles.__bottomBlock,
                        ]}
                    >
                        <View
                            style={[
                                styles.bottomLink,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__bottomLink,
                            ]}
                        >
                            <LabelXSmall>
                                {doNotTranslate`© ${currentYear} Khan Academy`}
                            </LabelXSmall>
                        </View>
                        {/* sync-start:legal-links 1711849506 ./apps/khanacademy/src/content-library/ui/legal-cell.tsx */}
                        <View
                            style={[
                                styles.bottomLink,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__bottomLink,
                            ]}
                        >
                            <LabelXSmall>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/docs/khan-academy-terms-of-service"
                                >
                                    {t`Terms of use`}
                                </Link>
                            </LabelXSmall>
                        </View>
                        <View
                            style={[
                                styles.bottomLink,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__bottomLink,
                            ]}
                        >
                            <LabelXSmall>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/privacy-policy"
                                >
                                    {t`Privacy Policy`}
                                </Link>
                            </LabelXSmall>
                        </View>
                        <View
                            style={[
                                styles.bottomLink,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__bottomLink,
                            ]}
                        >
                            <LabelXSmall>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/cookie-policy"
                                >
                                    {t`Cookie Notice`}
                                </Link>
                            </LabelXSmall>
                        </View>
                        <View
                            style={[
                                styles.bottomLink,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__bottomLink,
                            ]}
                        >
                            <LabelXSmall>
                                <Link
                                    style={actionStyles.inverse}
                                    href="/about/accessibility-statement"
                                >
                                    <Trans>Accessibility Statement</Trans>
                                </Link>
                            </LabelXSmall>
                        </View>
                        {/* sync-end:legal-links */}
                        <Spring />

                        <View
                            style={[
                                styles.socialMediaContainer,
                                this.props.khanmigoSidebarOpen &&
                                    styles.__socialMediaContainer,
                            ]}
                        >
                            <Link
                                href={facebookUrl}
                                aria-label={t`Follow us on Facebook`}
                                style={styles.socialIconLink}
                                onClick={(e) => {
                                    this.handleSocialMediaClick(
                                        e,
                                        "Facebook-Join-Header",
                                    );
                                }}
                            >
                                <PhosphorIcon
                                    size="medium"
                                    icon={facebookIcon}
                                />
                            </Link>
                            <Link
                                href={twitterUrl}
                                aria-label={t`Follow us on Twitter`}
                                style={styles.socialIconLink}
                                onClick={(e) => {
                                    this.handleSocialMediaClick(
                                        e,
                                        "Twitter-Follow-Header",
                                    );
                                }}
                            >
                                <PhosphorIcon
                                    size="medium"
                                    icon={twitterIcon}
                                />
                            </Link>
                            <Link
                                href={instagramUrl}
                                aria-label={t`Follow us on Instagram`}
                                style={styles.socialIconLink}
                                onClick={(e) => {
                                    this.handleSocialMediaClick(
                                        e,
                                        "Instagram-Follow-Header",
                                    );
                                }}
                            >
                                <PhosphorIcon
                                    size="medium"
                                    icon={instagramIcon}
                                />
                            </Link>
                        </View>
                    </View>
                </div>
            </footer>
        );
    }
}

const verticalSpacingBase = spacing.large_24;
const mainLinkHeight = 32;
const rootVerticalPadding = 12;

/**
 * Styles prefixed with "__" are for the guide open state.
 *
 * We apply these styles in order to move the responsive breakpoints
 * from `smOrSmaller` to `lgOrSmaller` to accommodate the space taken up in
 * the viewport by the sidebar.
 */
const styles = StyleSheet.create({
    appStoreBadge: {
        // Both Apple and Google require clear space around their badges
        // equal to one-quarter the badge height. Each badge is rendered
        // with a fixed width of 100px. This leaves the height variable,
        // but roughly around the low 30pxs meaning about 10px of clear
        // space is needed. The list items the badges appear within are
        // already padded by 8px so only 2px of additional margin is
        // needed to meet the requirements.
        //
        // Ideally, the list items would use margin instead of padding
        // to separate themselves so the required margin could be
        // specified fully here and allow collapsing margins to find
        // the minimum additional required.
        marginBlock: spacing.xxxxSmall_2,
        ":first-of-type": {
            marginTop: 0,
        },
        ":last-of-type": {
            marginBottom: 0,
        },
    },
    rootStyle: {
        backgroundColor: color.darkBlue,
        clear: "both",
        color: NON_LINK_COLOR,
        position: "relative",
        // The horizontal padding in the SiteFooter component needs to be kept in sync with the FooterAttribution component.
        padding: `${rootVerticalPadding}px 20px`,

        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",

        "@media print": {
            // SiteFooter is not included in printable reports
            display: "none",
        },

        [mediaQueries.smOrSmaller]: {
            padding: "0 20px",
        },
    },
    __rootStyle: {
        [mediaQueries.lgOrSmaller]: {
            padding: "0 20px",
        },
    },
    footerContainer: {
        boxSizing: "border-box",
        maxWidth: maxContainerWidth,
        position: "relative",
        margin: "0 auto",

        // The horizontal padding in the SiteFooter component needs to be kept in sync with the FooterAttribution component.
        padding: "0 20px",
        [mediaQueries.smOrSmaller]: {
            padding: 0,
        },
    },
    __footerContainer: {
        [mediaQueries.lgOrSmaller]: {
            padding: 0,
        },
    },

    // Where the main links and mission statement lies.
    mainBlock: {
        padding: `${spacing.xLarge_32}px 0 ${verticalSpacingBase}px`,

        position: "relative",
        display: "flex",
        flexDirection: "row",

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
    __mainBlock: {
        [mediaQueries.lgOrSmaller]: {
            flexDirection: "column",
        },
    },

    // That strip at the bottom with legalese and social links.
    bottomBlock: {
        flexDirection: "row",
        alignItems: "center",
        borderTop: `1px solid ${color.offBlack16}`,
        paddingBlock: spacing.xSmall_8,

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
            justifyContent: "center",
            paddingTop: verticalSpacingBase,
        },
    },
    __bottomBlock: {
        [mediaQueries.lgOrSmaller]: {
            flexDirection: "column",
            justifyContent: "center",
            paddingTop: verticalSpacingBase,
        },
    },

    li: {
        paddingRight: spacing.xSmall_8,
        paddingBottom: spacing.xSmall_8,
        minHeight: mainLinkHeight,
        boxSizing: "border-box",
    },

    heading: {
        textTransform: "initial",
    },

    missionAndLanguageSection: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "40%",
        paddingRight: 40,
        paddingBottom: 2 * verticalSpacingBase,

        [mediaQueries.smOrSmaller]: {
            width: "auto",
            height: "auto",

            textAlign: "center",
            paddingRight: 0,
        },
    },
    __missionAndLanguageSection: {
        [mediaQueries.lgOrSmaller]: {
            width: "auto",
            height: "auto",

            textAlign: "center",
            paddingRight: 0,
        },
    },

    missionParagraph: {
        margin: 0,
        maxWidth: 440,
        paddingBottom: spacing.xSmall_8,
        [mediaQueries.smOrSmaller]: {
            margin: "0 auto",
        },
    },
    __missionParagraph: {
        [mediaQueries.lgOrSmaller]: {
            margin: "0 auto",
        },
    },

    mainLinksSection: {
        fontSize: font.size.xSmall,
        lineHeight: font.lineHeight.xSmall,

        position: "relative",
        flexGrow: 1,

        columnCount: 3,

        [mediaQueries.smOrSmaller]: {
            columnCount: 2,
        },
    },
    __mainLinksSection: {
        [mediaQueries.lgOrSmaller]: {
            columnCount: 2,
        },
    },

    linkGroup: {
        breakInside: "avoid-column",

        // Note: it's important this is padding and not margin, because
        // layout engines seem to push margins to the next column when there's
        // a column break, creating an awkward space at the top of a column (
        // which we never want).
        // Also note: this is intentionally the same as the height of a single
        // link, so that the middle column has its bottom lined up with
        // the first column (given the number of links in there right now).
        paddingBottom: mainLinkHeight,
    },

    socialMediaContainer: {
        flexDirection: "row",
        [mediaQueries.smOrSmaller]: {
            marginTop: verticalSpacingBase,
        },
    },
    __socialMediaContainer: {
        [mediaQueries.lgOrSmaller]: {
            marginTop: verticalSpacingBase,
        },
    },

    socialIconLink: {
        color: color.white,
        opacity: 0.6,
        ":not(:first-child)": {
            marginLeft: 8,
        },

        ":hover": {
            opacity: 1,
        },
        display: "inline-block",
    },

    bottomLink: {
        ":not(:first-child)": {
            marginLeft: spacing.medium_16,
        },

        [mediaQueries.smOrSmaller]: {
            ":not(:first-child)": {
                marginLeft: 0,
            },
            marginTop: spacing.xSmall_8,
        },
    },
    __bottomLink: {
        [mediaQueries.lgOrSmaller]: {
            ":not(:first-child)": {
                marginLeft: 0,
            },
            marginTop: spacing.xSmall_8,
        },
    },

    // language and international SEO footer are positioned together,
    // using absolute positioning so that it can "move" from
    // the bottom left area on normal viewports, but move down to below
    // the main links area on smaller phone screens. Natural layout systems like
    // flex-box, unfortunately requires that the DOM order be changed in order
    // to achieve what we want, so it seems simpler to just position absolute
    // and make sure the appropriate containers have padding.
    languageAndInternationalWrapper: {
        position: "absolute",
        left: 0,
        bottom: verticalSpacingBase,
        [mediaQueries.smOrSmaller]: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
        },
    },
    __languageAndInternationalWrapper: {
        [mediaQueries.lgOrSmaller]: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
        },
    },

    languagePickerContainer: {
        [mediaQueries.smOrSmaller]: {
            width: "100%",
            textAlign: "center",
        },
    },
    __languagePickerContainer: {
        [mediaQueries.lgOrSmaller]: {
            width: "100%",
            textAlign: "center",
        },
    },

    // Apply styles to intl SEO footer links so they look similar to language picker
    countryLinkContainer: {
        paddingTop: 10,
        [mediaQueries.smOrSmaller]: {
            display: "flex",
            justifyContent: "center",
            width: "100%",
        },
    },
    __countryLinkContainer: {
        [mediaQueries.lgOrSmaller]: {
            display: "flex",
            justifyContent: "center",
            width: "100%",
        },
    },
});

export default SiteFooter;
